import { createMemo, createSignal, For, Show, type JSX } from 'solid-js';
import { ErrorAlert, LoadingRows } from '../components/states';
import { ApiError } from '../lib/api';
import { STACK_TYPES } from '../lib/catalog';
import { flatten, useCreateStack, useStacks } from '../lib/queries';
import { pushToast } from '../lib/toast';
import type { StackType } from '../lib/types';
import { Badge, Tag } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Input, Select } from '../ui/Field';
import { Icon } from '../ui/Icon';

/** The slug must satisfy the backend's SName rules
 *  (`validate`, rust_code/shared/src/sname.rs) — it names an S3 prefix and ends up in
 *  shell commands. */
const SAFE_NAME = /^[A-Za-z0-9._-]{1,128}$/;

export function AdminStacks(): JSX.Element {
  const stacks = useStacks(true);
  const create = useCreateStack();
  const items = createMemo(() => flatten(stacks.data?.pages));
  const unregistered = createMemo(() => items().filter((stack) => stack.manifest === 'none'));

  const [open, setOpen] = createSignal(false);
  const [display, setDisplay] = createSignal('');
  const [slug, setSlug] = createSignal('');
  const [type, setType] = createSignal<StackType>('DockerCompose');
  const [revision, setRevision] = createSignal('');
  const [touched, setTouched] = createSignal(false);
  /** Set when the form was opened from an existing prefix: that prefix already
   *  names the stack, so the slug is shown but not editable. */
  const [fixedSlug, setFixedSlug] = createSignal(false);

  const errors = createMemo(() => ({
    display: display().trim() ? undefined : 'Required.',
    slug: SAFE_NAME.test(slug().trim()) ? undefined : 'Letters, digits, dot, dash and underscore only.',
    revision: revision().trim() ? undefined : 'Required.',
  }));
  const valid = () => Object.values(errors()).every((error) => error === undefined);

  function reset(): void {
    setDisplay('');
    setSlug('');
    setType('DockerCompose');
    setRevision('');
    setTouched(false);
    setFixedSlug(false);
    setOpen(false);
  }

  function openBlank(): void {
    reset();
    setOpen(true);
  }

  /** Writes the manifest into a prefix that has none, so only the slug is known. */
  function openForPrefix(prefixSlug: string): void {
    reset();
    setSlug(prefixSlug);
    setFixedSlug(true);
    setOpen(true);
  }

  async function submit(): Promise<void> {
    setTouched(true);
    if (!valid()) return;
    try {
      await create.mutateAsync({
        display: display().trim(),
        slug: slug().trim(),
        type: type(),
        revision: revision().trim(),
      });
      pushToast('success', `Stack "${display().trim()}" registered.`);
      reset();
    } catch {
      // Shown in the dialog.
    }
  }

  const shown = (field: keyof ReturnType<typeof errors>) => (touched() ? errors()[field] : undefined);

  return (
    <main class="page page--narrow">
      <div class="page__head">
        <div class="page__title">
          <h1>Administration</h1>
          <span class="page__subtitle">Register the Docker Compose stacks offered in the catalog.</span>
        </div>
        <div class="page__actions">
          <Button onClick={openBlank}>
            <Icon name="plus" />
            Register stack
          </Button>
        </div>
      </div>

      <div class="alert alert--warning" style={{ 'margin-bottom': 'var(--space-6)' }}>
        <Icon name="shield" size={18} />
        <div class="alert__body">
          <span class="alert__title">Shared catalog</span>
          <span>Stacks registered here are offered to everyone using the workbench.</span>
        </div>
      </div>

      <Show when={stacks.isError}>
        <ErrorAlert title="Could not load the stacks bucket" error={stacks.error} onRetry={() => void stacks.refetch()} />
      </Show>

      <Show when={unregistered().length > 0}>
        <div class="alert alert--info" style={{ 'margin-bottom': 'var(--space-6)' }}>
          <Icon name="alert" size={18} />
          <div class="alert__body">
            <span class="alert__title">
              {unregistered().length === 1
                ? '1 prefix has no manifest'
                : `${unregistered().length} prefixes have no manifest`}
            </span>
            <span>Select one below to write its manifest and add it to the catalog.</span>
          </div>
        </div>
      </Show>

      <h3 style={{ margin: '0 0 var(--space-4)' }}>Stacks in the bucket</h3>
      <Show when={!stacks.isPending} fallback={<LoadingRows rows={2} height={72} />}>
        <Show when={items().length > 0} fallback={<span class="muted text-sm">The stacks bucket is empty.</span>}>
          <div class="list">
            <For each={items()}>
              {(stack) => (
                <Show
                  when={stack.manifest === '1' ? stack : undefined}
                  fallback={
                    // Clicking registers this prefix: the slug is already known,
                    // only the manifest contents are missing.
                    <button
                      type="button"
                      class="list-row list-row--action"
                      onClick={() => openForPrefix(stack.slug)}
                    >
                      <div class="list-row__main">
                        <span class="list-row__title mono">{stack.slug}</span>
                        <span class="list-row__meta">No readable manifest.aiwb.json in this prefix.</span>
                      </div>
                      <Badge tone="warning">unregistered</Badge>
                      <Icon name="chevronRight" size={18} />
                    </button>
                  }
                >
                  {(valid) => (
                    <div class="list-row">
                      <div class="list-row__main">
                        <span class="list-row__title">{valid().display}</span>
                        <span class="list-row__meta mono">{valid().slug}</span>
                      </div>
                      <Tag>{valid().type === 'DockerCompose' ? 'Docker Compose' : valid().type}</Tag>
                      <Badge tone="brand">rev {valid().revision}</Badge>
                    </div>
                  )}
                </Show>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <Show when={stacks.hasNextPage}>
        <div class="row gap-3" style={{ 'justify-content': 'center', 'margin-top': 'var(--space-6)' }}>
          <Button variant="secondary" loading={stacks.isFetchingNextPage} onClick={() => void stacks.fetchNextPage()}>
            Load more
          </Button>
        </div>
      </Show>

      <Dialog
        open={open()}
        onClose={reset}
        title={fixedSlug() ? `Register ${slug()}` : 'Register a stack'}
        description={
          fixedSlug()
            ? 'Writes manifest.aiwb.json into this prefix, which puts it in the catalog.'
            : 'The prefix must already hold the stack files in the stacks bucket.'
        }
        width={560}
        footer={
          <>
            <Button variant="secondary" onClick={reset} disabled={create.isPending}>
              Cancel
            </Button>
            <Button loading={create.isPending} onClick={submit}>
              Register stack
            </Button>
          </>
        }
      >
        <div class="stack gap-4">
          <Input
            label="Slug"
            placeholder="ollama"
            mono
            value={slug()}
            disabled={fixedSlug()}
            onInput={(event) => setSlug(event.currentTarget.value)}
            error={fixedSlug() ? undefined : shown('slug')}
            helperText={
              fixedSlug()
                ? 'Taken from the selected prefix.'
                : "Names the stack's prefix in the stacks bucket, and the directory it installs to."
            }
          />
          <Input
            label="Display name"
            placeholder="Ollama + Open WebUI"
            value={display()}
            onInput={(event) => setDisplay(event.currentTarget.value)}
            error={shown('display')}
          />
          <Select
            label="Type"
            options={STACK_TYPES}
            value={type()}
            onChange={(event) => setType(event.currentTarget.value as StackType)}
            disabled={STACK_TYPES.length === 1}
          />
          <Input
            label="Revision"
            placeholder="2026-09-01"
            value={revision()}
            onInput={(event) => setRevision(event.currentTarget.value)}
            error={shown('revision')}
          />
          <Show when={create.isError}>
            <ErrorAlert
              title="Could not register the stack"
              error={create.error}
              message={
                // The backend answers 404 when the prefix holds no files.
                create.error instanceof ApiError && create.error.status === 404
                  ? `The stacks bucket has no files under "${create.variables?.slug}". Upload the stack files there first, then register it.`
                  : undefined
              }
            />
          </Show>
        </div>
      </Dialog>
    </main>
  );
}
